import { useRoute } from '#imports'
import { computed } from 'vue'

import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'

export const BATTERY_CONFIDENCE_QUERY_PARAM =
  'battery_confidence_variant' as const

export type BatteryConfidenceVariant =
  | 'noVariation'
  | 'withText'
  | 'withNumbers'

export type BatteryConfidenceVariantNumeric = 1 | 2 | 3

type BatteryConfidenceVariantQueryParam =
  | BatteryConfidenceVariant
  | BatteryConfidenceVariantNumeric

const QUERY_PARAM_TO_VARIANT: Readonly<
  Record<BatteryConfidenceVariantQueryParam, BatteryConfidenceVariant>
> = {
  noVariation: 'noVariation',
  withText: 'withText',
  withNumbers: 'withNumbers',
  '1': 'noVariation',
  '2': 'withText',
  '3': 'withNumbers',
}

const VARIANT_TO_NUMERIC: Readonly<
  Record<BatteryConfidenceVariant, BatteryConfidenceVariantNumeric>
> = {
  noVariation: 1,
  withText: 2,
  withNumbers: 3,
}

/**
 * Resolves the battery confidence A/B/C test variant (PPI-7928) by merging the
 * experiment assignment with an optional QA query-param override
 * (`battery_confidence_variant`). The query param accepts both string variant
 * names (`noVariation`, `withText`, `withNumbers`) and their numeric equivalents
 * (`1`, `2`, `3`). When present and valid it takes precedence over the experiment.
 */
export function useBatteryConfidenceExperiment(route = useRoute()) {
  const experiments = useExperiments()

  const variant = computed<BatteryConfidenceVariant>(() => {
    const rawQuery = route.query?.[BATTERY_CONFIDENCE_QUERY_PARAM]
    const queryValue = Array.isArray(rawQuery) ? rawQuery[0] : rawQuery

    if (queryValue && queryValue in QUERY_PARAM_TO_VARIANT) {
      return QUERY_PARAM_TO_VARIANT[
        queryValue as BatteryConfidenceVariantQueryParam
      ]
    }

    const expValue = experiments['experiment.ppBatteryConfidence']

    return expValue === 'withText' || expValue === 'withNumbers'
      ? expValue
      : 'noVariation'
  })

  const numericVariant = computed(() => VARIANT_TO_NUMERIC[variant.value])
  const isControl = computed(() => variant.value === 'noVariation')
  const isWithText = computed(() => variant.value === 'withText')
  const isWithNumbers = computed(() => variant.value === 'withNumbers')

  return {
    variant,
    numericVariant,
    isControl,
    isWithText,
    isWithNumbers,
  }
}
